/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable padding-line-between-statements */
/* eslint-disable complexity */
import { RealtyObjectType } from '@search/filter-enums/enums/RealtyObjectType';
import { DealType } from '@search/filter-enums/enums/DealType';
import { yMapVectorUnpack, yMapVectorPack } from '@search/common-core/ymap/Vector';
import {
    arrayStrToNum,
    numToStr,
    queryParamtoArray,
    strToNum,
    Tokens,
    strToBool,
    boolToStr,
    Route,
    strToNumRequired,
    unknownToNil,
    SchemaTypeError
} from '@search/router/src';
import { regionsWithMetroEnumeration } from '@search/filter-enums/enums/Region';
import { AuthorTypeOptions } from '@search/filter-enums/enums/AuthorTypeOptions';
import { Rooms } from '@search/filter-enums/enums/Rooms';
import { CommissioningEnum } from '@search/graphql-typings';
import { commissioningMapper, newBuildingTypeMapper, quarterMapper } from '../mappers/newBuilding/mappers';
import {
    bathroomMapper,
    wallsMapper,
    categoryTypeMapper, dealTypeMapper, getDistrictMapperByRegion, getMetroMapperByRegion,
    renovationMapper, roomMapper, newBuildingMapper, authorTypeMapper,
    sellTypeMapper,
    livingFacilityMapper,
    furnitureMapper,
    bankPledgeMapper,
    getCityMapperByRegion,
    layoutImageMapper,
    buildEpochMapper,
    finishingMapper
} from '../mappers';

import { RouterContext } from '../RouterProvider';
import { parkingsMapper } from '../mappers/parkingsMapper';
import { livingTenantMapper } from '../mappers/livingTenantMapper';
import { mskRayonMapper } from '../mappers/regions/msk/mskRayonMapper';
import { sortOffersMapper } from '../mappers/sortOffersMapper';
import { windowsDirectionMapper } from '../mappers/windowsDirectionMapper';
import { ClassifiedRoute } from '../ClassifiedRoute';
import { seoPlaceUrlParams } from '../seoPlaceUrlParams';
import { mskMetroBranchMapper, stationsIdsByBranchSeo } from '../mappers/regions/msk';
import { determineMetroBranch } from '../mappers/determineMetroBranch';
import { SearchSeoType } from './SearchSeoType';
import { SearchType } from './SearchType';
import { searchDefaults } from './searchDefaults';
import { SearchSeoChpuMapper } from './SearchSeoChpuMapper';
import { PriceSeoMatcher } from './PriceSeoMatcher';
import { BuildEpochSeoMatcher } from './BuildEpochSeoMatcher';

export type ISearchType = ReturnType<typeof SearchType>
export type ISearchSeoType = ReturnType<typeof SearchSeoType>

function thousandsMapper(price: number) {
    return {
        id: price,
        value: price * 1e3,
        mantissa: 'thousand',
        slug: `${price * 1e3}-rub`,
        title: `до ${price} тыс. руб.`
    };
}

function millionsMapper(price: number) {
    return {
        id: price,
        value: price * 1e6,
        mantissa: 'million',
        slug: `${price}-mln-rub`,
        title: `до ${price} млн. руб.`
    };
}

export function getPricePreset({ dealType, categoryType } : {
    dealType: DealType;
    categoryType: RealtyObjectType;
}) {
    if (dealType === DealType.SELL && categoryType === RealtyObjectType.LAND) {
        return [ 1, 2, 3, 4, 5 ].map(millionsMapper);
    }

    if (dealType === DealType.SELL) {
        return [ 1, 2, 3, 4, 5, 6, 7, 8, 10, 15, 20 ].map(millionsMapper);
    }

    if (dealType === DealType.RENT && categoryType === RealtyObjectType.FLAT) {
        return [ 10, 15, 20, 25, 30, 35, 40, 45, 50, 60 ].map(thousandsMapper);
    }

    if (dealType === DealType.RENT && categoryType === RealtyObjectType.ROOM) {
        return [ 5, 10, 15, 20 ].map(thousandsMapper);
    }

    if (dealType === DealType.RENT && categoryType === RealtyObjectType.HOUSE) {
        return [ 5, 10, 15, 20, 25, 30 ].map(thousandsMapper);
    }
}

export class SearchRoute extends ClassifiedRoute<ISearchSeoType, ISearchType, typeof searchDefaults> {
    displayName = 'SearchRoute';

    protected metadata = SearchSeoType.config;

    protected patternCommon(p: Tokens<ISearchSeoType>) {
        return `${this.patternHost()}(/${p.region})/nedvizhimost`;
    }

    protected pattern(p: Tokens<ISearchSeoType>) {
        return `${this.patternCommon(p)}(/${p.geoSlug1})(/${p.geoSlug2})/${
            p.dealType}-${p.categoryType}(/${p.room
        })(/${p.newBuildingType}-${p.newBuildingName}-${p.newBuildingId
        })(/metro-${p.metroSegment})(/${p.metroBranch})(/rayon-${p.rayon})(/${
            p.okrug}-okrug)(/remont-${p.renovationSegment})(/${p.newSegment
        })(/${p.priceMaxSegment})(-rub)(/${p.bankPledgeOptionsSegment})(/${
            p.seoMortgageAllowedSegment})(/${
            p.bathroomSegment})(/${p.parkingsSegment})(/${p.authorSegment})(/${p.houseCategorySegment})(/${
            p.landStatusSegment})(/${p.rentPrepaymentSegment})(/${p.nearMetroSegment})(/${p.communications})(/${
            p.seoSegPrice})(/${p.seoSeg1})(/${p.seoSeg2})(/${p.seoSegBuildEpoch})(/${p.citySegment})/`;
    }

    protected conditions() {
        return {
            ...super.conditions(),
            seoSeg1: SearchSeoChpuMapper.chpuKeys,
            seoSeg2: SearchSeoChpuMapper.chpuKeys,
            seoSegPrice: PriceSeoMatcher.$.chpuKeys,
            seoMortgageAllowedSegment: [ 'v-ipoteku' ],
            authorSegment: [ 'ot-sobstvennika' ],
            parkingsSegment: [ 'parkovka', 'parkovka-podzemnaya' ],
            houseCategorySegment: [ 'taunkhaus', 'dupleks', 'chast-doma' ],
            landStatusSegment: [ 'izhs', 'snt-i-dnp', 'lpkh', 'pmzh' ],
            rentPrepaymentSegment: [ 'bez-predoplaty' ],
            bathroomSegment: bathroomMapper.keys,
            priceMaxSegment: '\\d+(?:-mln)?-rub',
            room: [ ...roomMapper.keys, '4-komnaty_i_bolee' ],
            new: newBuildingMapper.keys,
            geoSlug1: '[\\w\\d\\-_]+',
            geoSlug2: '[\\w\\d\\-_]+',
            categoryType: categoryTypeMapper.keys,
            dealType: dealTypeMapper.keys,
            newBuildingType: newBuildingTypeMapper.keys,
            newSegment: newBuildingMapper.keys,
            bankPledgeOptionsSegment: bankPledgeMapper.keys,
            newBuildingId: '\\d+',
            nearMetroSegment: [ 'ryadom-s-metro' ],
            communications: [ 's-kommunikaciyami' ],
            seoSegBuildEpoch: BuildEpochSeoMatcher.$.chpuKeys,
            metroBranch: mskMetroBranchMapper.keys
        };
    }

    defaults() {
        if (this.context?.isMobile) {
            return {
                ...searchDefaults,
                pageSize: 10 as number
            } as const;
        }

        return searchDefaults;
    }

    protected checkKarta(seo: ISearchSeoType) {
        if (seo.geoSlug1 === 'karta' || seo.geoSlug2 === 'karta') throw new SchemaTypeError('geoSlug1 === karta');
    }

    protected fromQuery(seo: ISearchSeoType) {
        this.checkKarta(seo);
        const raw = SearchSeoType(seo);

        const defaults = this.defaults();
        const dealType = raw.dealType ? dealTypeMapper.typeFromSeo(raw.dealType) : defaults.dealType;
        const categoryType = raw.categoryType ?
            categoryTypeMapper.typeFromSeo(raw.categoryType) : defaults.categoryType;
        const roomType = typeof raw.rooms === 'string' ? roomMapper.typeFromSeo(raw.rooms) : undefined;

        const rec = new SearchSeoChpuMapper({
            categoryType,
            dealType,
            rooms: roomType ? [ roomType ] : undefined,
            kitchenAreaMin: raw.kitchenAreaMin,
            last: raw.last,
            // eslint-disable-next-line max-len
            livingTenantOptions: raw.livingTenantOptions as readonly ('UNKNOWN' | 'CHILDREN_ALLOWED' | 'PETS_ALLOWED')[],
            commisionType: raw.commisionType
        }).fromChpu([ raw.seoSeg1, raw.seoSeg2 ]);

        const chpu = rec.params;

        const region = this.regionId(raw.region) ?? this.regionIdDefault();
        const metroMapper = getMetroMapperByRegion(region);

        const hasBounds = raw.lx !== undefined && raw.ly !== undefined && raw.rx !== undefined && raw.ry !== undefined;

        const districtIds = [] as number[];
        const rayonOrOkrug = raw.rayon || raw.okrug;

        const districtMapper = getDistrictMapperByRegion(region);
        const rayonOrOkrugId = districtMapper?.typeFromSeoOptional(rayonOrOkrug) ??
            mskRayonMapper.typeFromSeoOptional(rayonOrOkrug);
        if (rayonOrOkrugId) districtIds.push(rayonOrOkrugId);

        const addresses = [] as number[];

        const citySlug = raw.geoSlug1 ?? raw.citySegment;
        const cityMapper = getCityMapperByRegion(region);
        const cityId = cityMapper?.typeFromSeoOptional(citySlug);
        if (! cityId && citySlug === 'zelenograd') districtIds.push(390);
        if (cityId) districtIds.push(cityId);

        // if (raw.geoSlug1 && ! cityId) geoSlugs.push(raw.geoSlug1);
        // if (raw.geoSlug2) geoSlugs.push(raw.geoSlug2);

        const geoSlugs = [] as string[];

        for (const slug of [ raw.geoSlug1, raw.geoSlug2 ]) {
            if (! slug) continue;
            if (slug === citySlug && cityId) continue;
            const geo = this.context.rootRegions?.slugs.id(slug, region);
            if (geo) {
                const isDistrict = geo.kind === 'DISTRICT' || geo.kind === 'LOCALITY';
                if (cityId === geo.id || rayonOrOkrugId === geo.id) continue;
                if (isDistrict) {
                    if (districtIds.includes(geo.id)) continue;
                    districtIds.push(geo.id);
                } else addresses.push(geo.id);
            } else {
                geoSlugs.push(slug);
            }
        }

        const districtIdsRaw = raw.districtIds instanceof Array || ! raw.districtIds ?
            raw.districtIds as (string[] | undefined) :
            [ raw.districtIds as string ];

        for (const rawDistrictId of districtIdsRaw ?? []) {
            const id = Number(rawDistrictId);

            if (Number.isNaN(id)) continue;
            districtIds.push(id);
        }

        const some = Object.keys(raw).filter(k => (raw as any)[k] !== undefined &&
            k !== 'region' &&
            k !== 'room' &&
            k !== 'geoSlug2' &&
            k !== 'geoSlug1' &&
            k !== 'dealType' &&
            k !== 'categoryType' &&
            k !== 'newBuildingType' &&
            k !== 'newBuildingName' &&
            k !== 'newBuildingId' &&
            k !== 'metroSegment' &&
            k !== 'metroBranch' &&
            k !== 'rayon' &&
            k !== 'okrug' &&
            k !== 'renovationSegment' &&
            k !== 'bathroomSegment' &&
            k !== 'parkingsSegment' &&
            k !== 'newSegment' &&
            k !== 'authorSegment' &&
            k !== 'seoMortgageAllowedSegment' &&
            k !== 'bankPledgeOptionsSegment' &&
            k !== 'houseCategorySegment' &&
            k !== 'landStatusSegment' &&
            k !== 'rentPrepaymentSegment' &&
            k !== 'pageNumber' &&
            k !== 'priceMaxSegment' &&
            k !== 'nearMetroSegment' &&
            k !== 'seoSegPrice' &&
            k !== 'seoSeg1' &&
            k !== 'seoSeg2' &&
            k !== 'origin' &&
            k !== 'dachaSegment' &&
            k !== 'seoSegBuildEpoch'
        );
        const isGetEmpty = some.length === 0;

        let tagCount = 0;

        const bathroom = bathroomMapper.toEnums(raw.bathroomSegment ? [ raw.bathroomSegment ] : raw.bathroom);
        let parkings = parkingsMapper.toEnums(raw.parkings);

        if (raw.parkingsSegment === 'parkovka' && parkings.length === 0) parkings = [ 'CLOSED', 'UNDERGROUND', 'OPEN' ];
        if (raw.parkingsSegment === 'parkovka-podzemnaya' && parkings.length === 0) parkings = [ 'UNDERGROUND' ];

        let author = authorTypeMapper.toEnums(raw.author);

        // @ts-ignore
        if (raw.authorSegment === 'ot-sobstvennika' && author.length === 0) author = [ AuthorTypeOptions.OWNER ];

        let houseCategory = chpu.houseCategory ?? queryParamtoArray(raw.houseCategory);

        if (houseCategory.length === 0) {
            if (raw.houseCategorySegment === 'taunkhaus') houseCategory = [ 'TOWNHOUSE' ];
            if (raw.houseCategorySegment === 'dupleks') houseCategory = [ 'DUPLEX' ];
            if (raw.houseCategorySegment === 'chast-doma') houseCategory = [ 'PART_OF_HOUSE' ];
        }

        let landStatus = chpu.landStatus ?? queryParamtoArray(raw.landStatus);

        if (landStatus.length === 0) {
            if (raw.landStatusSegment === 'izhs') landStatus = [ 'IZHS' ];
            if (raw.landStatusSegment === 'snt-i-dnp') landStatus = [ 'SNT', 'DNP' ];
            if (raw.landStatusSegment === 'lpkh') landStatus = [ 'AGRICULTURAL' ];
            if (raw.landStatusSegment === 'pmzh') landStatus = [ 'SNT', 'DNP', 'IZHS' ];
        }
        let rentPrepayment = raw.rentPrepayment;

        if (raw.rentPrepaymentSegment === 'bez-predoplaty') rentPrepayment = 'NO_PREPAYMENT';

        if (isGetEmpty) {
            if (raw.rentPrepaymentSegment) tagCount++;
            if (raw.houseCategorySegment) tagCount++;
            if (raw.landStatusSegment) tagCount++;
            if (raw.room) tagCount++;
            if (raw.newSegment) tagCount++;
            if (raw.renovationSegment) tagCount++;
            if (raw.bankPledgeOptions) tagCount++;
            if (raw.bathroomSegment) tagCount++;
            if (raw.parkingsSegment) tagCount++;
            if (raw.authorSegment) tagCount++;
            if (raw.seoMortgageAllowedSegment) tagCount++;
            if (raw.priceMaxSegment) tagCount++;
            if (raw.nearMetroSegment) tagCount++;
            if (raw.seoSegPrice) tagCount++;
            if (raw.seoSegBuildEpoch) tagCount++;
        }

        let priceMax = strToNum(raw.priceMax);
        let priceMin = strToNum(raw.priceMin);

        const priceMaxSegmentNormalized = strToNum(raw.priceMaxSegment?.replace(/^(\d+)-.*/, '$1'));

        const preset = getPricePreset({
            dealType,
            categoryType
        })?.find(p => p.id === priceMaxSegmentNormalized || p.value === priceMaxSegmentNormalized);

        if (preset) priceMax = preset.value;

        let transport = raw.transport;
        let transportTimeLimit = raw.transportTimeLimit;

        let builtYearMin = strToNum(raw.builtYearMin);
        let builtYearMax = strToNum(raw.builtYearMax);

        if (raw.nearMetroSegment) {
            transport = 'FOOT';
            transportTimeLimit = 'MAX_15_MINUTES';
        }

        const priceSeo = raw.seoSegPrice ? PriceSeoMatcher.$.match({
            categoryType,
            dealType,
            region,
            priceMax: null,
            priceMin: null,
            regionTranslit: this.context.rootRegions?.byId(region)?.translit,
            seg: raw.seoSegPrice
        }) : undefined;

        if (priceSeo) {
            priceMax = priceSeo.params.priceMax ?? priceMax;
            priceMin = priceSeo.params.priceMin ?? priceMin;
        }

        let rooms = roomMapper.toEnums(raw.rooms);

        if (raw.room === '4-komnaty_i_bolee') {
            rooms = [ Rooms.ROOM_4, Rooms.ROOM_5_AND_MORE ];
        } else if (raw.room) {
            rooms = [ roomMapper.typeFromSeo(raw.room) ];
        }

        const metroSegment = raw.metroSegment;
        const ms = metroSegment && isGetEmpty ? metroMapper?.typeFromSeoOptional(metroSegment) : undefined;

        const buildEpochSeo = raw.seoSegBuildEpoch ? BuildEpochSeoMatcher.$.match({
            categoryType,
            dealType,
            builtYearMin: null,
            builtYearMax: null,
            seg: raw.seoSegBuildEpoch
        }) : undefined;

        if (buildEpochSeo) {
            builtYearMin = buildEpochSeo.params.builtYearMin ?? builtYearMin;
            builtYearMax = buildEpochSeo.params.builtYearMax ?? builtYearMax;
        }

        const metroBranch = raw.metroBranch ? mskMetroBranchMapper.typeFromSeo(raw.metroBranch) : null;

        const segments: NonNullable<ISearchType['segments']> = {
            seoMortgageAllowed: Boolean(raw.seoMortgageAllowedSegment),

            author: isGetEmpty && raw.authorSegment ? author : [],
            parkings: isGetEmpty && raw.parkingsSegment ? parkings : [],
            bathroom: isGetEmpty && raw.bathroomSegment ? bathroom : [],
            rentPrepayment: isGetEmpty && raw.rentPrepaymentSegment ? rentPrepayment : undefined,
            addresses: isGetEmpty ? addresses.slice() : [],
            districtIds: isGetEmpty ? districtIds.slice() : [],
            metroIds: ms ? [ Number(ms) ] : [],
            metroBranch,
            isGetEmpty,
            tagCount: tagCount + rec.chpuUsedTagsCount,
            newBuilding: raw.newSegment && isGetEmpty ? [ newBuildingMapper.typeFromSeo(raw.newSegment) ] : [],
            renovation: raw.renovationSegment && isGetEmpty ?
                [ renovationMapper.typeFromSeo(raw.renovationSegment) ] :
                [],
            bankPledgeOptions: raw.bankPledgeOptionsSegment && isGetEmpty ?
                bankPledgeMapper.typeFromSeo(raw.bankPledgeOptionsSegment) :
                undefined,
            priceMax: isGetEmpty ? priceMax as any : undefined,
            priceMin: isGetEmpty ? priceMin : undefined,
            transport: isGetEmpty && transport ? transport : undefined,
            transportTimeLimit: isGetEmpty && transportTimeLimit ? transportTimeLimit : undefined,
            ...chpu,
            furniture: chpu.furniture ?? [],
            houseCategory: chpu.houseCategory ?? (isGetEmpty && raw.houseCategorySegment ? houseCategory : []),
            landStatus: chpu.landStatus ?? (isGetEmpty && raw.landStatusSegment ? landStatus : []),
            rooms: chpu.rooms ?? (raw.room && isGetEmpty ? rooms : []),
            builtYearMin: isGetEmpty ? builtYearMin : undefined,
            builtYearMax: isGetEmpty ? builtYearMax : undefined,
            communications: raw.communications
        };

        const metroIdsFromBranch = raw.metroBranch ? stationsIdsByBranchSeo.get(raw.metroBranch) : null;

        const metroIds = metroIdsFromBranch ?? metroMapper?.toIds(raw.metroIds, metroSegment) ?? [];

        const metroIdsRaw = raw.metroIds instanceof Array || ! raw.metroIds ?
            raw.metroIds as string[] :
            [ raw.metroIds as string ];

        for (const metroIdRaw of metroIdsRaw ?? []) {
            const id = Number(metroIdRaw);

            if (Number.isNaN(id) || metroIds.includes(id)) continue;

            metroIds.push(id);
        }

        for (const addressId of arrayStrToNum(raw.addresses)) {
            addresses.push(addressId);
        }

        const {
            newBuildingIds,
            newBuildingId,
            newBuildingType,
            newBuildingName
        } = raw;
        let newBuildingParams;

        if (newBuildingId && newBuildingName && newBuildingType) {
            newBuildingParams = {
                newBuildingType,
                newBuildingName,
                newBuildingIds: [ strToNumRequired(newBuildingId) ]
            };
        } else {
            newBuildingParams = {
                newBuildingIds: arrayStrToNum(newBuildingIds)
            };
        }
        const bankPledgeOptions = raw.bankPledgeOptionsSegment ?? raw.bankPledgeOptions;

        let commissioning = raw.commissioning;

        if (commissioning) {
            if (commissioning.indexOf('q') !== -1) {
                const [ year, quarter ] = commissioning.split('q');
                const quarterNumber = parseInt(quarter, 10) as 1 | 2 | 3 | 4;

                commissioning = `${quarterMapper.seoFromType(quarterNumber)}_${year}`;
            } else {
                if (commissioningMapper.hasSeo(commissioning)) {
                    commissioning = commissioningMapper.typeFromSeo(commissioning);
                } else {
                    const [ quarter, year ] = commissioning.split('_');

                    if (quarter && year) {
                        commissioning = `${quarterMapper.typeFromSeo(quarter)}_${year}`;
                    }
                }
            }
        }

        let gas = raw.gas;
        let water = raw.water;
        let sewerage = raw.sewerage;
        let electricity = raw.electricity;

        if (raw.communications) {
            gas = [ 'HAS_ANY' ];
            water = [ 'HAS_ANY' ];
            sewerage = [ 'HAS_ANY' ];
            electricity = [ 'HAS_ANY' ];
        }

        return SearchType({
            ...defaults,
            geoSlugs,
            segments,
            seoMortgageAllowed: Boolean(raw.seoMortgageAllowed ?? raw.seoMortgageAllowedSegment),
            region,

            rooms: chpu.rooms ?? rooms,
            renovation: renovationMapper.toEnums(raw.renovation, raw.renovationSegment),
            finishingType: finishingMapper.toEnums(raw.finishingType),
            commissioning,
            bankPledgeOptions: bankPledgeOptions ? bankPledgeMapper.typeFromSeo(bankPledgeOptions) : undefined,
            newBuilding: newBuildingMapper.toEnums(raw.newBuilding, raw.newSegment),

            roomsInOffer: arrayStrToNum(raw.roomsInOffer),

            addresses,
            metroIds,
            metroBranch,
            districtIds,

            ...newBuildingParams,

            dealType,
            categoryType,

            totalAreaMin: strToNum(raw.totalAreaMin),
            totalAreaMax: chpu.totalAreaMax ?? strToNum(raw.totalAreaMax),

            landAreaMin: chpu.landAreaMin ?? strToNum(raw.landAreaMin),
            landAreaMax: chpu.landAreaMax ?? strToNum(raw.landAreaMax),

            houseCategory,
            houseAreaMin: strToNum(raw.houseAreaMin),
            houseAreaMax: strToNum(raw.houseAreaMax),
            bathroomLocation: chpu.bathroomLocation ?? queryParamtoArray(raw.bathroomLocation),

            priceMin,
            priceMax,
            mortgagePayment: strToNum(raw.mortgagePayment),
            priceUnit: raw.priceUnit,

            livingAreaMin: strToNum(raw.livingAreaMin),
            livingAreaMax: strToNum(raw.livingAreaMax),

            kitchenAreaMin: chpu.kitchenAreaMin ?? strToNum(raw.kitchenAreaMin),
            kitchenAreaMax: strToNum(raw.kitchenAreaMax),

            builtYearMin,
            builtYearMax,

            floorMin: chpu.floorMin ?? strToNum(raw.floorMin),
            floorMax: chpu.floorMax ?? strToNum(raw.floorMax),

            floorsTotalMin: chpu.floorsTotalMin ?? strToNum(raw.floorsTotalMin),
            floorsTotalMax: chpu.floorsTotalMax ?? strToNum(raw.floorsTotalMax),
            last: chpu.last ?? (raw.last || undefined),
            first: raw.first || undefined,

            cellHeight: chpu.cellHeight ?? raw.cellHeight,
            bathroom,
            landStatus,

            heating: chpu.heating ?? queryParamtoArray(raw.heating),
            gas: chpu.gas ?? queryParamtoArray(gas),
            water: chpu.water ?? queryParamtoArray(water),
            electricity: chpu.electricity ?? queryParamtoArray(electricity),
            sewerage: chpu.sewerage ?? queryParamtoArray(sewerage),

            // @deprecated use transport & transportTimeLimit
            transportType: transport, // raw.transportType ??
            // @deprecated use transport & transportTimeLimit
            timeLimit: transportTimeLimit, // raw.timeLimit ??
            transport,
            transportTimeLimit,

            livingFacility: livingFacilityMapper.toEnums(raw.livingFacility),
            furniture: chpu.furniture ?? furnitureMapper.toEnums(raw.furniture),
            livingTenantOptions: chpu.livingTenantOptions ?? livingTenantMapper.toEnums(raw.livingTenantOptions),
            utilitiesIncluded: strToBool(raw.utilitiesIncluded) || undefined,
            depositType: chpu.commisionType || chpu.livingTenantOptions ?
                raw.depositType : chpu.depositType ?? raw.depositType,
            commisionType: chpu.commisionType ?? raw.commisionType,
            rentPrepayment,
            walls: wallsMapper.toEnums(raw.walls),
            parkings,
            author,
            sellType: sellTypeMapper.toEnums(raw.sellType),
            balcony: chpu.balcony ?? raw.balcony,
            hasLift: chpu.hasLift ?? raw.hasLift,
            photoOptions: raw.photoOptions,
            renovationProgram: chpu.renovationProgram ?? raw.renovationProgram,
            nearbyOptions: chpu.nearbyOptions ?? raw.nearbyOptions,
            sellerOrganizationName: raw.sellerOrganizationName,
            layoutImageOptions: layoutImageMapper.toEnums(raw.layoutImageOptions),
            apartments: chpu.apartments ?? raw.apartments,
            infrastructure: chpu.infrastructure ?? queryParamtoArray(raw.infrastructure) ?? [],

            windowsDirection: windowsDirectionMapper.toEnums(raw.windowsDirection),

            buildEpoch: buildEpochMapper.toEnums(raw.buildEpoch),

            onlineView: raw.onlineView,

            pageNumber: strToNum(raw.pageNumber) || defaults.pageNumber,
            pageSize: strToNum(raw.pageSize) || defaults.pageSize,
            sort: raw.sort ? sortOffersMapper.typeFromSeo(raw.sort) : 'unknown',

            tile: raw.x !== undefined && raw.y !== undefined && raw.z !== undefined ? {
                x: Number(raw.x),
                y: Number(raw.y),
                z: Number(raw.z)
            } : undefined,

            selectedOffers: raw.selectedOffers ? queryParamtoArray(raw.selectedOffers) : undefined,

            mapBounds: hasBounds || raw.pol || raw.geohash ? {
                geohash: raw.geohash ?? undefined,
                bounds: hasBounds ? {
                    min: {
                        x: Number(raw.lx),
                        y: Number(raw.ly)
                    },
                    max: {
                        x: Number(raw.rx),
                        y: Number(raw.ry)
                    }
                } : undefined,
                polygon: raw.pol ? yMapVectorUnpack(raw.pol) : []
            } : undefined
        }) as ISearchType;
    }

    // eslint-disable-next-line complexity
    protected toQuery(p: ISearchType) {
        const defaults = this.defaults();
        const { bounds, polygon, geohash } = p.mapBounds ?? {};

        const slugs = this.context.rootRegions?.slugs;
        const geosAll = [
            ...p.districtIds.map(id => ({ id, kind: 'DISTRICT', translit: undefined as undefined | string })),
            ...p.addresses.map(id => ({ id, kind: 'STREET', translit: undefined as undefined | string })),
            ...p.metroIds.map(id => ({ id, kind: 'METRO', translit: undefined as undefined | string }))
        ];

        for (const slug of p.geoSlugs ?? []) {
            const g = this.context.rootRegions?.slugs.id(slug, p.region);
            if (g && ! p.districtIds.includes(g.id)) {
                geosAll.push({
                    id: g.id,
                    kind: g.kind ?? 'DISTRICT',
                    translit: undefined
                });
            }
        }

        const rootInGeos = geosAll.find(geo => this.context.rootRegions?.byId(geo.id));
        let regionRaw = p.region;

        if (rootInGeos?.id) {
            regionRaw = rootInGeos.id;
        }

        const geos = geosAll.filter(({ id }) => id !== regionRaw)
            .map(geoRaw => slugs?.slug(geoRaw.id) ?? geoRaw);

        if (slugs && p.geoSlugs) {
            for (const geoSlug of p.geoSlugs) {
                const geo = slugs.id(geoSlug, regionRaw);
                if (! geo) continue;
                if (geos.find(item => item.id === geo.id)) continue;

                geos.push(geo);
            }
        }

        const isServer = this.context.isServer;
        const {
            segmentSlugs: place,
            queryParams: {
                districtIds,
                addresses,
                metroIds
            },
            regionId: region,
        } = seoPlaceUrlParams({
            geos,
            regionIdSuggested: regionRaw,
            isCanonical: this.context.isCanonical,
            isServer,
            urlMapper: this.context.urlMapper,
            newBuildingType: p.newBuildingType
        });

        const newBuildingId = p.newBuildingIds.length === 1 ? p.newBuildingIds[0] : undefined;
        const newBuildingSeoParams = {
            newBuildingType: newBuildingId ? newBuildingTypeMapper.seoFromType(p.newBuildingType ?? 'zhk') : undefined,
            newBuildingName: newBuildingId ? (p.newBuildingName ?? 'nazvaniye') : undefined,
            newBuildingId: newBuildingId ? numToStr(newBuildingId) : undefined,
            newBuildingIds: p.newBuildingIds.length > 1 ? p.newBuildingIds.map(String) : undefined
        };

        const isZhk = p.newBuildingType === 'zhk';
        const categoryType = p.categoryType || defaults.categoryType;

        let bankPledgeOptionsSegment = p.bankPledgeOptions ?
            bankPledgeMapper.seoFromType(p.bankPledgeOptions) :
            undefined;
        let bankPledgeOptions = bankPledgeOptionsSegment;

        let newSegment = p.newBuilding.length === 1 && ! isZhk ?
            newBuildingMapper.seoFromType(p.newBuilding[0]) :
            undefined;
        let newBuilding = isZhk && isServer ? [] : p.newBuilding.map(newBuildingMapper.seoFromType);

        let renovation = p.renovation.map(renovationMapper.seoFromType);
        let renovationSegment = p.renovation.length === 1 ?
            renovationMapper.seoFromType(p.renovation[0]) :
            undefined;

        const finishingType = p.finishingType.map(finishingMapper.seoFromType);

        //  sorting for correct chpu comparison
        (p.landStatus as (typeof p['landStatus'][number][])).sort();

        const searchSeoChpu = new SearchSeoChpuMapper(p).toChpu(p);
        let { chpuParams, chpuKeys } = searchSeoChpu;
        const { chpuKeysTagsCount } = searchSeoChpu;

        let room: undefined | string =
            (! chpuParams.rooms && p.rooms.length === 1 && p.categoryType !== RealtyObjectType.ROOM) ?
                roomMapper.seoFromType(p.rooms[0]) :
                undefined;

        if (
            p.categoryType === RealtyObjectType.HOUSE &&
            p.rooms.some(r => r === Rooms.ROOM_4 || r === Rooms.ROOM_5_AND_MORE)
        ) {
            room = '4-komnaty_i_bolee';
        }

        const apartments = chpuParams.apartments ? undefined : unknownToNil(p.apartments);

        if (p.kitchenAreaMin === 17 && p.rooms.length && apartments) {
            room = undefined;
        }

        let rooms = p.rooms.map(roomMapper.seoFromType);

        let bathroom = p.bathroom.map(bathroomMapper.seoFromType);
        let bathroomSegment = bathroom.length === 1 ? bathroom[0] : undefined;

        if (p.bathroom[0] === 'SEVERAL') bathroomSegment = undefined;

        let parkings = p.parkings.map(parkingsMapper.seoFromType);
        let parkingsSegment = parkings.length === 3 ? 'parkovka' : undefined;

        if (parkings.length === 1 && parkings[0] === 'UNDERGROUND') parkingsSegment = 'parkovka-podzemnaya';

        let author = p.author.map(authorTypeMapper.seoFromType);
        // @ts-ignore
        let authorSegment = p.author.length === 1 && p.author[0] === AuthorTypeOptions.OWNER ?
            'ot-sobstvennika' :
            undefined;

        let tagCount = chpuKeysTagsCount;

        let seoMortgageAllowed = p.seoMortgageAllowed && p.dealType === DealType.SELL ? 'v-ipoteku' : undefined;
        let seoMortgageAllowedSegment = seoMortgageAllowed;

        const isHouse = p.categoryType === RealtyObjectType.HOUSE;
        const isLand = p.categoryType === RealtyObjectType.LAND;

        let houseCategory = chpuParams.houseCategory || ! (isHouse || isLand) ? [] : p.houseCategory;
        let houseCategorySegment: string | undefined;

        if (houseCategory?.length === 1) {
            if (houseCategory[0] === 'TOWNHOUSE') houseCategorySegment = 'taunkhaus';
            if (houseCategory[0] === 'DUPLEX') houseCategorySegment = 'dupleks';
            if (houseCategory[0] === 'PART_OF_HOUSE') houseCategorySegment = 'chast-doma';
        }

        let builtYearMin = p.builtYearMin;
        let builtYearMax = p.builtYearMax;

        const buildEpochSeo = (p.builtYearMin && p.builtYearMax) && ! houseCategorySegment ?
            BuildEpochSeoMatcher.$.match({
                categoryType: p.categoryType,
                dealType: p.dealType,
                builtYearMin: p.builtYearMin,
                builtYearMax: p.builtYearMax
            }) : undefined;

        let seoSegBuildEpoch = buildEpochSeo?.seg;

        let landStatus = chpuParams.landStatus || ! (isHouse || isLand) ? [] : p.landStatus;
        let landStatusSegment: string | undefined;

        if (landStatus.length === 1) {
            if (landStatus[0] === 'IZHS') landStatusSegment = 'izhs';
            if (landStatus[0] === 'AGRICULTURAL') landStatusSegment = 'lpkh';
        }
        const isSntDnp = landStatus.includes('SNT') && landStatus.includes('DNP');

        if (isSntDnp && landStatus.length === 2) landStatusSegment = 'snt-i-dnp';
        if (isSntDnp && landStatus.length === 3 && landStatus.includes('IZHS')) landStatusSegment = 'pmzh';

        let rentPrepayment = p.rentPrepayment;
        let rentPrepaymentSegment: string | undefined;

        if (rentPrepayment === 'NO_PREPAYMENT') rentPrepaymentSegment = 'bez-predoplaty';

        let priceMax = p.priceMax;
        let priceMin = p.priceMin;
        const priceMaxPreset = priceMax ? getPricePreset(p)?.find(mp => mp.value === priceMax) : undefined;
        let priceMaxSegment = priceMaxPreset ? priceMaxPreset.slug : undefined;

        let transport = unknownToNil(p.transport) ?? unknownToNil(p.transportType);
        let transportTimeLimit = unknownToNil(p.transportTimeLimit) ?? unknownToNil(p.timeLimit);
        let nearMetroSegment =
            p.transport === 'FOOT' &&
            p.transportTimeLimit === 'MAX_15_MINUTES' &&
            regionsWithMetroEnumeration.has(regionRaw) ?
                'ryadom-s-metro' :
                undefined;

        const priceSeo = (p.priceMax || p.priceMin) && ! houseCategorySegment ? PriceSeoMatcher.$.match({
            categoryType: p.categoryType,
            dealType: p.dealType,
            priceMax: p.priceMax,
            priceMin: p.priceMin,
            region,
            regionTranslit: this.context.rootRegions?.byId(region)?.translit,
        }) : undefined;

        let seoSegPrice = priceSeo?.seg;

        if (! regionsWithMetroEnumeration.has(regionRaw)) {
            transport = undefined;
            transportTimeLimit = undefined;
        }
        if (seoMortgageAllowedSegment) bankPledgeOptionsSegment = undefined;

        if (room) tagCount++;
        if (houseCategorySegment) tagCount++;
        if (newSegment) tagCount++;
        if (renovationSegment) tagCount++;
        if (bankPledgeOptionsSegment) tagCount++;
        if (bathroomSegment) tagCount++;
        if (parkingsSegment) tagCount++;
        if (authorSegment) tagCount++;
        if (seoMortgageAllowedSegment) tagCount++;
        if (landStatusSegment) tagCount++;
        if (rentPrepaymentSegment) tagCount++;
        if (priceMaxSegment) tagCount++;
        if (nearMetroSegment) tagCount++;
        if (seoSegPrice) tagCount++;
        if (seoSegBuildEpoch) tagCount++;

        if (tagCount > 2) {
            priceMaxSegment = undefined;
            room = undefined;
            newSegment = undefined;
            renovationSegment = undefined;
            bankPledgeOptionsSegment = undefined;
            bathroomSegment = undefined;
            parkingsSegment = undefined;
            authorSegment = undefined;
            seoMortgageAllowedSegment = undefined;
            houseCategorySegment = undefined;
            landStatusSegment = undefined;
            rentPrepaymentSegment = undefined;
            seoSegPrice = undefined;
            nearMetroSegment = undefined;
            chpuParams = {};
            chpuKeys = [];
            seoSegBuildEpoch = undefined;
        }

        let gas = chpuParams.gas ? undefined : p.gas;
        let water = chpuParams.water ? undefined : p.water;
        const heating = chpuParams.heating ? undefined : p.heating;
        let electricity = chpuParams.electricity ? undefined : p.electricity;
        let sewerage = chpuParams.sewerage ? undefined : p.sewerage;

        const communications = (
            gas?.[0] &&
            water?.[0] &&
            electricity?.[0] &&
            sewerage?.[0]
        ) ? 's-kommunikaciyami' : undefined;

        if (communications) {
            gas = undefined;
            water = undefined;
            electricity = undefined;
            sewerage = undefined;
        }

        if (chpuKeys.includes('cottage')) {
            houseCategorySegment = undefined;
            landStatusSegment = undefined;
        }

        if (room) rooms = [];

        if (newSegment) newBuilding = [];
        if (renovationSegment) renovation = [];
        if (bankPledgeOptionsSegment) bankPledgeOptions = undefined;
        if (bathroomSegment) bathroom = [];
        if (parkingsSegment) parkings = [];
        if (authorSegment) author = [];
        if (seoMortgageAllowedSegment) seoMortgageAllowed = undefined;
        if (houseCategorySegment) houseCategory = [];
        if (landStatusSegment) landStatus = [];
        if (rentPrepaymentSegment) rentPrepayment = undefined;
        if (place.metro || place.rayon) nearMetroSegment = undefined;

        if (nearMetroSegment) {
            transportTimeLimit = undefined;
            transport = undefined;
        }

        if (priceMaxSegment || (seoSegPrice && priceSeo?.params.priceMax)) priceMax = undefined;
        if (seoSegPrice && priceSeo?.params.priceMin) priceMin = undefined;

        if (categoryType === RealtyObjectType.ROOM) {
            room = undefined;
            newSegment = undefined;
            bankPledgeOptionsSegment = undefined;
            if (
                renovationSegment ||
                chpuParams.apartments ||
                bathroomSegment ||
                parkingsSegment ||
                authorSegment
            ) seoMortgageAllowedSegment = undefined;
        }

        if (seoSegBuildEpoch && buildEpochSeo?.params.builtYearMin && buildEpochSeo.params.builtYearMax) {
            builtYearMin = undefined;
            builtYearMax = undefined;
        }

        const metroBranch = determineMetroBranch(metroIds);

        let commissioning;

        if (p.commissioning && p.commissioning !== CommissioningEnum.Unknown) {
            if (commissioningMapper.hasType(p.commissioning)) {
                commissioning = commissioningMapper.seoFromType(p.commissioning);
            } else {
                const [ quarter, year ] = p.commissioning.split('_');
                commissioning = [ year, quarterMapper.typeFromSeo(quarter) ].join('q');
            }
        }

        // SearchSeoType
        return ({
            ...this.regionParams(region),
            seoSeg1: chpuKeys[0],
            seoSeg2: chpuKeys[1],
            rayon: place.rayon,
            okrug: place.okrug,
            geoSlug1: place.geoSlug1,
            geoSlug2: place.geoSlug2,
            districtIds: districtIds.map(String),
            addresses: addresses.map(String),
            room: chpuParams.rooms ? undefined : room,
            rooms: chpuParams.rooms ? undefined : rooms,
            renovationSegment,
            renovation,
            bankPledgeOptionsSegment,
            bankPledgeOptions,
            newSegment,
            newBuilding,
            seoSegPrice,
            commissioning,

            roomsInOffer: p.roomsInOffer.map(String),

            metroSegment: place.metro,
            metroIds: metroBranch ? [] : metroIds.map(String),
            metroBranch,

            ...newBuildingSeoParams,

            dealType: dealTypeMapper.seoFromType(p.dealType || defaults.dealType),
            categoryType: categoryTypeMapper.seoFromType(categoryType),

            totalAreaMin: numToStr(p.totalAreaMin),
            totalAreaMax: chpuParams.totalAreaMax ? undefined : numToStr(p.totalAreaMax),

            landAreaMin: chpuParams.landAreaMin ? undefined : numToStr(p.landAreaMin),
            landAreaMax: chpuParams.landAreaMax ? undefined : numToStr(p.landAreaMax),

            houseCategory,
            houseCategorySegment,
            houseAreaMin: numToStr(p.houseAreaMin),
            houseAreaMax: numToStr(p.houseAreaMax),
            bathroomLocation: chpuParams.bathroomLocation ? undefined : p.bathroomLocation,

            priceMin: numToStr(priceMin),
            priceMax: numToStr(priceMax),
            mortgagePayment: numToStr(p.mortgagePayment),
            priceMaxSegment,
            priceUnit: p.priceUnit && p.priceUnit !== defaults.priceUnit ? p.priceUnit : undefined,

            livingAreaMin: numToStr(p.livingAreaMin),
            livingAreaMax: numToStr(p.livingAreaMax),

            kitchenAreaMin: chpuParams.kitchenAreaMin ?
                undefined :
                numToStr(p.kitchenAreaMin),
            kitchenAreaMax: numToStr(p.kitchenAreaMax),

            seoSegBuildEpoch,
            builtYearMin: numToStr(builtYearMin),
            builtYearMax: numToStr(builtYearMax),

            floorMin: chpuParams.floorMin ? undefined : numToStr(p.floorMin),
            floorMax: chpuParams.floorMax ? undefined : numToStr(p.floorMax),

            floorsTotalMin: chpuParams.floorsTotalMin ? undefined : numToStr(p.floorsTotalMin),
            floorsTotalMax: chpuParams.floorsTotalMax ? undefined : numToStr(p.floorsTotalMax),
            last: chpuParams.last ? undefined : (p.last || undefined),
            first: p.first || undefined,

            cellHeight: chpuParams.cellHeight ? undefined : p.cellHeight,

            bathroom,
            bathroomSegment,
            landStatus,
            landStatusSegment,

            heating,
            gas,
            water,
            electricity,
            sewerage,
            communications,

            rentPrepaymentSegment,

            transport,
            transportTimeLimit,
            nearMetroSegment,
            cotedjSegment: chpuKeys.includes('cottage') ? undefined : null,

            livingFacility: p.livingFacility.map(livingFacilityMapper.seoFromType),
            furniture: chpuParams.furniture ? undefined : p.furniture.map(furnitureMapper.seoFromType),
            finishingType,
            livingTenantOptions: chpuParams.livingTenantOptions ?
                undefined :
                p.livingTenantOptions.map(livingTenantMapper.seoFromType),
            utilitiesIncluded: boolToStr(p.utilitiesIncluded) || undefined,
            depositType: chpuParams.depositType && ! chpuParams.commisionType && ! chpuParams.livingTenantOptions ?
                undefined : p.depositType,
            commisionType: chpuParams.commisionType ? undefined : p.commisionType,
            rentPrepayment,
            walls: p.walls.map(wallsMapper.seoFromType),
            parkings,
            parkingsSegment,
            author,
            authorSegment,
            seoMortgageAllowed,
            seoMortgageAllowedSegment,
            sellType: p.sellType.map(sellTypeMapper.seoFromType),
            balcony: chpuParams.balcony ? undefined : unknownToNil(p.balcony),
            photoOptions: unknownToNil(p.photoOptions),
            renovationProgram: chpuParams.renovationProgram ? undefined : unknownToNil(p.renovationProgram),
            nearbyOptions: chpuParams.nearbyOptions ? undefined : unknownToNil(p.nearbyOptions),
            sellerOrganizationName: p.sellerOrganizationName,
            layoutImageOptions: p.layoutImageOptions ?
                p.layoutImageOptions.map(layoutImageMapper.seoFromType) : undefined,
            apartments,
            hasLift: chpuParams.hasLift ? undefined : unknownToNil(p.hasLift),
            infrastructure: chpuParams.infrastructure ? [] : p.infrastructure,

            windowsDirection: p.windowsDirection.map(windowsDirectionMapper.SeoType),

            buildEpoch: p.buildEpoch?.map(buildEpochMapper.seoFromType) ?? [],

            onlineView: unknownToNil(p.onlineView),

            pageNumber: p.pageNumber && p.pageNumber !== defaults.pageNumber ? numToStr(p.pageNumber) : undefined,
            pageSize: p.pageSize && p.pageSize !== defaults.pageSize ? numToStr(p.pageSize) : undefined,
            sort: p.sort ? sortOffersMapper.seoFromType(p.sort) || undefined : undefined,

            selectedOffers: p.selectedOffers?.length ? p.selectedOffers : undefined,

            x: numToStr(p.tile?.x),
            y: numToStr(p.tile?.y),
            z: numToStr(p.tile?.z),

            lx: bounds?.min.x.toString(),
            ly: bounds?.min.y.toString(),
            rx: bounds?.max.x.toString(),
            ry: bounds?.max.y.toString(),
            pol: polygon ? yMapVectorPack(polygon) : undefined,
            geohash: geohash || undefined,
            utm_source: p.utm_source,
            utm_medium: p.utm_medium,
            utm_campaign: p.utm_campaign,
            utm_term: p.utm_term,
            utm_content: p.utm_content
        });
    }
}

export type ISearchRoute = Route<ISearchType, RouterContext, typeof searchDefaults>;
